import React from "react";
import { ChevronRight, EyeOff, ExternalLink, GitBranch, Clock, Flame, Zap, AlertTriangle, Sparkles } from "lucide-react";
import SeverityBadge, { Severity, SeverityStrip } from "./SeverityBadge";

// Priority Score Badge Component
function PriorityBadge({ score }: { score: number }) {
  if (score === undefined || score === null) return null;
  
  let color = "bg-low/15 text-low";
  let label = "Low";
  
  if (score >= 80) {
    color = "bg-critical/15 text-critical";
    label = "Critical";
  } else if (score >= 60) {
    color = "bg-high/15 text-high";
    label = "High";
  } else if (score >= 40) {
    color = "bg-medium/15 text-medium";
    label = "Medium";
  }
  
  return (
    <span className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-bold ${color}`}>
      <Zap className="h-3 w-3" />
      P{score}
    </span>
  );
}

// KEV Badge Component
function KevBadge() {
  return (
    <span className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-bold bg-critical/20 text-critical ring-1 ring-critical/30">
      <Flame className="h-3 w-3" />
      KEV
    </span>
  );
}

// New Finding Badge
function NewBadge() {
  return (
    <span className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-bold bg-brand/15 text-brand ring-1 ring-brand/30">
      <Sparkles className="h-3 w-3" />
      NEW
    </span>
  );
}

export interface Finding {
  id: string;
  severity: Severity;
  title: string;
  ruleId?: string;
  tool: string;
  kind: "sast" | "sca" | "secret" | "iac" | "license" | "container";
  file?: string;
  line?: number;
  endLine?: number;
  status: "open" | "fixed" | "suppressed";
  firstSeen?: string;
  lastSeen?: string;
  // Code snippet for developer context
  snippet?: string;
  codeContext?: string;
  // P0 Features
  priorityScore?: number;  // 0-100
  riskScore?: number;      // 0-100
  isKev?: boolean;         // Known Exploited Vulnerability
  epssScore?: number;      // 0-1
  cvssScore?: number;      // 0-10
  cvssVector?: string;     // Full CVSS vector string
  cveId?: string;
  cweId?: string;
  isNew?: boolean;         // First seen in latest scan
  isReachable?: boolean;
  // Enhanced fields
  description?: string;
  fixGuidance?: string;
  owasp?: string[];           // OWASP Top 10 categories
  owaspNames?: string[];      // Full OWASP names
  confidence?: string;        // low, medium, high
  language?: string;
  fixVersion?: string;        // Version with fix for SCA
  references?: string[];
}

interface FindingRowProps {
  finding: Finding;
  onClick?: () => void;
  onSuppress?: () => void;
  selected?: boolean;
  compact?: boolean;
}

const kindLabels: Record<string, { label: string; color: string }> = {
  sast: { label: "SAST", color: "bg-brand/15 text-brand ring-brand/25" },
  sca: { label: "Dependencies", color: "bg-info/15 text-info ring-info/25" },
  secret: { label: "Secret", color: "bg-critical/15 text-critical ring-critical/25" },
  iac: { label: "IaC", color: "bg-medium/15 text-medium ring-medium/25" },
  license: { label: "License", color: "bg-low/15 text-low ring-low/25" },
  container: { label: "Container", color: "bg-high/15 text-high ring-high/25" },
};

const statusLabels: Record<string, { label: string; color: string }> = {
  open: { label: "Open", color: "bg-critical/12 text-critical ring-critical/25" },
  fixed: { label: "Fixed", color: "bg-success/12 text-success ring-success/25" },
  suppressed: { label: "Suppressed", color: "bg-muted/12 text-muted ring-muted/25" },
};

export default function FindingRow({
  finding,
  onClick,
  onSuppress,
  selected = false,
  compact = false,
}: FindingRowProps) {
  const kindConfig = kindLabels[finding.kind] || kindLabels.sast;
  const statusConfig = statusLabels[finding.status] || statusLabels.open;

  const paddingClass = compact ? "py-2" : "py-3";

  return (
    <tr
      className={`group cursor-pointer border-b border-divider transition-colors duration-150
        ${selected ? "bg-brand/5" : "hover:bg-surface2/60"}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick?.();
        }
      }}
    >
      {/* Severity strip */}
      <td className="w-1 p-0">
        <SeverityStrip severity={finding.severity} />
      </td>

      {/* Main content */}
      <td className={`px-4 ${paddingClass}`}>
        <div className="flex items-center gap-2 flex-wrap">
          {/* KEV Badge - highest priority */}
          {finding.isKev && <KevBadge />}
          
          {/* New finding badge */}
          {finding.isNew && <NewBadge />}
          
          {/* Priority Score */}
          {finding.priorityScore !== undefined && finding.priorityScore > 0 && (
            <PriorityBadge score={finding.priorityScore} />
          )}

          {/* Kind badge */}
          <span
            className={`inline-flex items-center rounded-md px-2 py-0.5 text-[10px] font-medium ring-1 ${kindConfig.color}`}
          >
            {kindConfig.label}
          </span>

          {/* CVE ID */}
          {finding.cveId && (
            <span className="text-xs text-critical font-mono bg-critical/10 px-1.5 py-0.5 rounded">
              {finding.cveId}
            </span>
          )}

          {/* Rule ID */}
          {finding.ruleId && !finding.cveId && (
            <span className="text-xs text-muted font-mono">{finding.ruleId}</span>
          )}
        </div>

        {/* Title */}
        <div className="mt-1 text-sm font-medium text-text group-hover:text-brand transition-colors">
          {finding.title}
        </div>

        {/* File path + EPSS/CVSS scores */}
        <div className="mt-1 flex items-center gap-3">
          {finding.file && (
            <span className="text-xs text-muted font-mono truncate max-w-sm">
              {finding.file}
              {finding.line && <span className="text-text2">:{finding.line}</span>}
            </span>
          )}
          
          {/* EPSS Score */}
          {finding.epssScore !== undefined && finding.epssScore > 0 && (
            <span className="text-xs text-muted" title="EPSS: Exploit Prediction Scoring System">
              EPSS: <span className="text-text2 font-medium">{(finding.epssScore * 100).toFixed(1)}%</span>
            </span>
          )}
          
          {/* CVSS Score */}
          {finding.cvssScore !== undefined && finding.cvssScore > 0 && (
            <span className="text-xs text-muted" title="CVSS Base Score">
              CVSS: <span className="text-text2 font-medium">{finding.cvssScore.toFixed(1)}</span>
            </span>
          )}
        </div>

        {/* Code snippet preview - helps developers quickly identify the issue */}
        {finding.snippet && !compact && (
          <div className="mt-2 p-2 bg-bg rounded border border-divider/50 overflow-hidden">
            <code className="text-[11px] font-mono text-text2/80 whitespace-pre-wrap line-clamp-2">
              {finding.snippet.trim().substring(0, 150)}
              {finding.snippet.length > 150 ? "..." : ""}
            </code>
          </div>
        )}
      </td>

      {/* Engine indicator - don't reveal actual tool names */}
      <td className={`px-4 ${paddingClass} hidden lg:table-cell`}>
        <span className="text-xs text-muted">Paxley Engine</span>
      </td>

      {/* Status */}
      <td className={`px-4 ${paddingClass}`}>
        <span
          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ${statusConfig.color}`}
        >
          {statusConfig.label}
        </span>
      </td>

      {/* Severity badge (visible on larger screens) */}
      <td className={`px-4 ${paddingClass} hidden md:table-cell`}>
        <SeverityBadge severity={finding.severity} size="sm" />
      </td>

      {/* Actions */}
      <td className={`px-4 ${paddingClass} text-right`}>
        <div className="inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          {onSuppress && finding.status === "open" && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onSuppress();
              }}
              className="btn-icon"
              aria-label="Suppress finding"
              title="Suppress"
            >
              <EyeOff className="h-4 w-4" />
            </button>
          )}
          <button className="btn-icon" aria-label="View details" title="View details">
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </td>
    </tr>
  );
}

// Card variant for mobile/responsive
export function FindingCard({
  finding,
  onClick,
  onSuppress,
}: Omit<FindingRowProps, "compact" | "selected">) {
  const kindConfig = kindLabels[finding.kind] || kindLabels.sast;
  const statusConfig = statusLabels[finding.status] || statusLabels.open;

  return (
    <div
      className="card-interactive p-4"
      onClick={onClick}
      role="button"
      tabIndex={0}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          {/* Tags row */}
          <div className="flex items-center gap-2 flex-wrap">
            {/* KEV Badge - highest priority */}
            {finding.isKev && <KevBadge />}
            
            {/* New finding badge */}
            {finding.isNew && <NewBadge />}
            
            <SeverityBadge severity={finding.severity} size="sm" />
            
            {/* Priority Score */}
            {finding.priorityScore !== undefined && finding.priorityScore > 0 && (
              <PriorityBadge score={finding.priorityScore} />
            )}
            
            <span
              className={`inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-medium ring-1 ${kindConfig.color}`}
            >
              {kindConfig.label}
            </span>
            <span
              className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium ring-1 ${statusConfig.color}`}
            >
              {statusConfig.label}
            </span>
          </div>

          {/* CVE ID if present */}
          {finding.cveId && (
            <div className="mt-1.5">
              <span className="text-xs text-critical font-mono bg-critical/10 px-1.5 py-0.5 rounded">
                {finding.cveId}
              </span>
            </div>
          )}

          {/* Title */}
          <div className="mt-2 text-sm font-medium text-text">{finding.title}</div>

          {/* File */}
          {finding.file && (
            <div className="mt-1 text-xs text-muted font-mono truncate">
              {finding.file}
              {finding.line && `:${finding.line}`}
            </div>
          )}

          {/* Meta - scores */}
          <div className="mt-2 flex items-center gap-3 text-xs text-muted flex-wrap">
            <span>Paxley Engine</span>
            {finding.epssScore !== undefined && finding.epssScore > 0 && (
              <>
                <span>•</span>
                <span>EPSS: {(finding.epssScore * 100).toFixed(1)}%</span>
              </>
            )}
            {finding.cvssScore !== undefined && finding.cvssScore > 0 && (
              <>
                <span>•</span>
                <span>CVSS: {finding.cvssScore.toFixed(1)}</span>
              </>
            )}
            {finding.ruleId && !finding.cveId && (
              <>
                <span>•</span>
                <span className="font-mono">{finding.ruleId}</span>
              </>
            )}
          </div>
        </div>

        <ChevronRight className="h-5 w-5 text-muted flex-shrink-0" />
      </div>
    </div>
  );
}
